import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import MetaTooltip from "./MetaTooltip";
import { medicineBand } from "./data";
import ModalPortal from "../common/ModalPortal";

/**
 * "View trend" modal for a Medicine.
 *
 * Audit columns (8): Timestamp · Since when · To when · Description ·
 * Reported by · Frequency · Instructions · Status. Dose column deliberately
 * dropped — the description carries any dose narrative that matters, and
 * omitting it reclaims horizontal room for the timestamp + reported-by cells.
 *
 * Each StatusEntry may carry 0..N AdherenceReviews. They render as
 * distinct full-width rows nested under the parent, spanning all 8 grid
 * columns — a different layout, deliberately, so the compliance story is
 * scannable at a glance.
 */

// Merged Frequency (Dose qty.) label — mirrors the table's
// frequencyDoseText: cadence + qty in one column, dose trailing in
// parens. `asNeeded` rewrites to "As needed" so the audit reads
// naturally instead of surfacing the raw enum key.
const slotLabel = (s) => (s === "asNeeded" ? "As needed" : s);
const fmtFreqDose = (row) => {
  if (!row) return "—";
  const f = row.doseFrequency || {};
  const dq = row.doseQuantity || {};
  const per = row.perSlotQuantities || {};
  const hasPerSlot = per && Object.keys(per).length > 0;
  const asNeededSlot =
    Array.isArray(f.timesOfDay) && f.timesOfDay.includes("asNeeded");

  const parens = (v, u) => {
    const t = `${v || ""} ${u || ""}`.trim();
    return t ? ` (${t})` : "";
  };

  if (hasPerSlot) {
    const order = ["Morning", "Afternoon", "Evening", "Night"];
    const parts = [];
    order.forEach((slot) => {
      const q = per[slot];
      if (!q) return;
      parts.push(`${slot}${parens(q.value, q.unit)}`);
    });
    if (parts.length > 0) return parts.join(", ");
  }
  if (asNeededSlot) return `As needed${parens(dq.value, dq.unit)}`;
  if (Array.isArray(f.timesOfDay) && f.timesOfDay.length > 0) {
    return `${f.timesOfDay.map(slotLabel).join(", ")}${parens(dq.value, dq.unit)}`;
  }
  if (f.customValue && f.customUnit) {
    const abbrev = { Hours: "hrs", Days: "days", Weeks: "wks", Months: "mos" }[f.customUnit] || f.customUnit;
    return `Every ${f.customValue} ${abbrev}${parens(dq.value, dq.unit)}`;
  }
  const scalar = parens(dq.value, dq.unit).trim();
  return scalar || "—";
};

// Map the instruction key ("before") back to its short "before meal" label.
// `local` matches the main-table label ("Local Application") verbatim so a
// reviewer who sees the sentence-case chip on the row then opens the
// trend audit sees the same string, not the raw enum key.
const INSTR_LABELS = {
  before: "before meal",
  with: "with meal",
  after: "after meal",
  "after-dinner": "after dinner",
  empty: "empty stomach",
  local: "Local Application",
};

// "27 Jul 2026" → same helper as other v2 modals so the mock's exact format
// falls out for free.
const OUTCOME_LABEL = {
  completed: "Completed",
  partially: "Partially completed",
  stopped_early: "Stopped early",
};
const OUTCOME_CLASS = {
  completed: "med-adh-pill-completed",
  partially: "med-adh-pill-partial",
  stopped_early: "med-adh-pill-stopped",
};

// "27 Jul 2026, 01:05 pm" — mirrors the mock string.
const dateAndTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${date}, ${h}:${String(m).padStart(2, "0")} ${ampm}`;
};
// Compute an end date from a start + whole-day duration (ZY-10312
// convention: start = day 1, so end = start + duration - 1). Null on
// bad input so the caller falls through to an "Ongoing" / "—" label.
const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate);
  if (isNaN(d.getTime()) || !days) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// ZY-10352 — "To when" cell for an audit row. Resolution order:
//   1. row.medEndDate stored on the entry (post-ZY-10352 writes
//      preserve it unchanged across in-place transitions).
//   2. derive from row.medStartDate + row.durationDays when the row
//      is bounded. Covers legacy rows whose backend didn't persist
//      medEndDate.
//   3. "Ongoing" for an ongoing-indefinite regimen.
//   4. "—" fallback for malformed rows.
const toWhenLabel = (row) => {
  if (row && row.medEndDate) return formatShortDate(row.medEndDate);
  if (row && row.ongoingIndefinitely) return "Ongoing";
  if (row && row.medStartDate && row.durationDays > 0) {
    const derived = addDaysISO(row.medStartDate, row.durationDays - 1);
    if (derived) return formatShortDate(derived);
  }
  return "—";
};

const timeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Reporter fallback — server enriches ReportedByName / ByName at read
// time; when empty (upstream down / unknown role) fall back to the
// deterministic "Employee #21" label so the cell always shows something.
const reporterLabel = (actor, name) => {
  if (name) return name;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

// ZY-10352 wave-2 — "(id) name" label format for the merged Created ON
// / Discontinued ON / Updated on cells. Falls back to just the name,
// or just the id, or "" when neither is known. A service actor renders
// as "System" so a cron-driven complete / delete reads obviously
// non-manual, matching entryReporterLabel's rule above.
const reporterIdLabel = (actor, name) => {
  if (!actor && !name) return "";
  if (actor && actor.who === "service") return "System";
  const id = actor && actor.whoId ? actor.whoId : "";
  const display = name
    || (actor && actor.who
      ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1)
      : "");
  if (id && display) return `(${id}) ${display}`;
  return display || (id ? `(${id})` : "");
};

const TrendModal = ({ patientId, medicine, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const svc = new MedicineService();
    svc.trend(
      patientId,
      medicine.medicineId,
      (res) => setData(res),
      (err) => setError((err && err.message) || "Failed to load trend")
    );
  }, [patientId, medicine.medicineId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const currentBand = data ? medicineBand(data.status || "ongoing") : null;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal cc-modal-trend med-modal-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">
                {medicine.medicine}
                {(medicine.formulationName || medicine.formulation) && (
                  <span className="med-form">
                    {" "}({medicine.formulationName || medicine.formulation})
                  </span>
                )}
              </span>
              {currentBand && (
                <span
                  className="cc-modal-tag"
                  style={{ color: currentBand.fg, background: currentBand.bg }}
                >
                  {currentBand.label}
                </span>
              )}
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          {!data && !error && <div className="cc-modal-body cc-modal-loading">Loading…</div>}
          {error && <div className="cc-modal-body cc-modal-error">{error}</div>}
  
          {data && (
            <div className="cc-modal-body">
              <div className="cc-trend-audit">
                <div className="cc-trend-audit-head">
                  <span>Audit</span>
                  <span className="cc-trend-audit-count">
                    · {data.audit ? data.audit.length : 0} entries
                  </span>
                </div>
                <div className="sec-table med-trend-grid" role="table">
                  {/* ZY-10352 wave-2 — column order per spec:
                      Created ON · Since when · To When · Discontinued ON
                      · Frequency (Dose qty.) · Instructions · Description
                      · Updated on · Status.
                      "Created ON" merges the old Timestamp + Reported by
                      into one date/time/actor stack; the standalone
                      "Reported by" column is retired.
                      "Discontinued ON" shows only on rows terminated by
                      a reviewer-driven Discontinue; implicit close-outs
                      from an ongoing→ongoing regimen edit leave it blank. */}
                  <div className="sec-thead" role="row">
                    <span role="columnheader">Created on</span>
                    <span role="columnheader">Since when</span>
                    <span role="columnheader">To when</span>
                    <span role="columnheader">Discontinued on</span>
                    <span role="columnheader">Frequency (Dose qty.)</span>
                    <span role="columnheader">Instructions</span>
                    <span role="columnheader">Description</span>
                    <span role="columnheader">Updated on</span>
                    <span role="columnheader">Status</span>
                  </div>
                  {/* Newest → oldest so the current regimen sits at the
                      top; the API doesn't index anything against this
                      order, so a plain reverse is safe. */}
                  {[...(data.audit || [])].reverse().map((row, idx) => {
                    const isDelete = row.event === "delete" || row.status === "deleted";
                    const isAuto = row.event === "auto-status";
                    const band = medicineBand(row.status || "ongoing");
                    const reviews = row.adherenceReviews || [];
                    const createdActor = isAuto
                      ? "System"
                      : reporterIdLabel(row.reportedBy, row.reportedByName);
                    const updatedActor = reporterIdLabel(row.updatedBy, row.updatedByName);
                    // Discontinued ON is populated only on reviewer-driven
                    // Discontinue rows (backend stamps DiscontinuedDate
                    // only there). The implicit close-out on an
                    // ongoing→ongoing regimen edit leaves it nil so this
                    // cell reads "—".
                    const hasDiscontinuedOn = row.status === "discontinued" && row.discontinuedDate;
                    return (
                      <React.Fragment key={idx}>
                        <div className="sec-row" role="row">
                          {/* Created ON (merged Timestamp + Reported by):
                              date / time / "(id) name". Three-line
                              stack reusing the Medicines Timestamp cell
                              styling. */}
                          <span className="sec-cell med-trend-ts" role="cell">
                            <span className="med-trend-ts-date">
                              {formatShortDate(row.recordedAt)}
                            </span>
                            <span className="med-trend-ts-time">{timeOnly(row.recordedAt)}</span>
                            <span className="med-trend-ts-time">{createdActor || "—"}</span>
                          </span>
                          {/* Since when = regimen period's own start date
                              (medStartDate), not the write-time sinceWhen. */}
                          <span className="sec-cell" role="cell">
                            {row.medStartDate ? formatShortDate(row.medStartDate) : "—"}
                          </span>
                          <span className="sec-cell" role="cell">
                            {toWhenLabel(row)}
                          </span>
                          {/* Discontinued ON: the clinical EFFECTIVE
                              date on top with the recording actor
                              below. Time is intentionally omitted
                              (unlike Created on / Updated on) — the
                              reviewer picks a date-only value, so a
                              timestamp here would be noise. "—" when
                              the row isn't a discontinue. */}
                          <span className="sec-cell med-trend-ts" role="cell">
                            {hasDiscontinuedOn ? (
                              <>
                                <span className="med-trend-ts-date">
                                  {formatShortDate(row.discontinuedDate)}
                                </span>
                                {updatedActor && (
                                  <span className="med-trend-ts-time">{updatedActor}</span>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </span>
                          <span className="sec-cell" role="cell">
                            {fmtFreqDose(row)}
                          </span>
                          <span className="sec-cell" role="cell">
                            {INSTR_LABELS[row.instructions] || row.instructions || "—"}
                          </span>
                          {/* Description clamps to two lines via the shared
                              MetaTooltip anchor / clamp classes; hovering
                              reveals the full text in a light-card popover. */}
                          <span className="sec-cell med3-trend-c-desc" role="cell">
                            <MetaTooltip text={row.description} />
                          </span>
                          {/* Updated on: date / time / "(id) name" stack
                              on every row that was transitioned out of
                              ongoing — reviewer-driven OR implicit (the
                              ongoing→ongoing close-out). Still-ongoing /
                              legacy rows render "—". */}
                          <span className="sec-cell med-trend-ts" role="cell">
                            {row.updatedAt ? (
                              <>
                                <span className="med-trend-ts-date">
                                  {formatShortDate(row.updatedAt)}
                                </span>
                                <span className="med-trend-ts-time">{timeOnly(row.updatedAt)}</span>
                                {updatedActor && (
                                  <span className="med-trend-ts-time">{updatedActor}</span>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </span>
                          <span className="sec-cell" role="cell">
                            {isDelete ? (
                              <span
                                className="cc-trend-band"
                                style={{ background: band.bg, color: band.fg }}
                              >
                                Deleted
                              </span>
                            ) : (
                              <span
                                className="cc-trend-band"
                                style={{ background: band.bg, color: band.fg }}
                                title={isAuto ? "Auto-completed by system on expiry" : undefined}
                              >
                                {isAuto ? "Auto-completed" : band.label}
                              </span>
                            )}
                          </span>
                        </div>
  
                        {reviews.map((r, ri) => (
                          <div className="med-trend-adh" role="row" key={`${idx}-adh-${ri}`}>
                            <div className="med-trend-adh-line1">
                              <span className="med-trend-adh-eyebrow">ADHERENCE</span>
                              <span className="med-trend-adh-when">· {dateAndTime(r.at)}</span>
                              <span className={`med-trend-adh-pill ${OUTCOME_CLASS[r.outcome] || ""}`}>
                                {OUTCOME_LABEL[r.outcome] || r.outcome}
                              </span>
                              <span className="med-trend-adh-window">
                                Start date: {formatShortDate(r.adherenceStart)}
                                <span className="med-trend-adh-window-sep">·</span>
                                End date: {formatShortDate(r.adherenceEnd)}
                              </span>
                            </div>
                            <div className="med-trend-adh-line2">
                              <span className="med-trend-adh-reporter">
                                {reporterLabel(r.by, r.byName)}
                              </span>
                              <span className="med-trend-adh-desc">
                                {r.description || "—"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  medicine: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
